/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { api } from "../../../api/GeneratedApi";
import { IntegrationAPI } from "../../../api/IntegrationAPI";
import { ProductsAPI } from "../../../api/Products";
import { TagApi } from "../../../api/Tags";
import { TagsAtom } from "../../../atoms/TagsAtom";
import { UserAtom } from "../../../atoms/UserAtom";
import {
  IProduct,
  IThrivecartIntegration,
  IThrivecartProduct,
  ProductType,
} from "../../../generated/Api";
import { SettingsTab } from "../../../pages/settings";
import { IOption } from "../../../types/CommonTypes";
import Warning from "../../Modal/container/Warning";
import StyledAutocompleteSelect from "../../StyledAutoCompleteSelect/AutoCompleteSelect";
import StyledButton from "../../StyledButton";
import { useModal } from "./../../Modal/Modal";
import { AllIntegrationsAtom } from "./Integrations";
import { ProductItem } from "./ProductItem";

const Container = styled.div`
  width: 1000px;
  height: fit-content;

  border: 1px solid #eaeaeabf;

  background-color: #ffffff;

  margin: 40px 0px;

  padding: 24px;

  cursor: default;
`;

const InfoColumn = styled.div`
  border-bottom: 1px solid #eaeaeabf;
  padding-bottom: 16px;

  display: flex;
  align-items: stretch;
  justify-content: space-between;

  > img {
    width: 67px;
    height: 67px;
  }
`;

const TextColumn = styled.div`
  max-width: 600px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const Title = styled.div`
  font-size: 18px;
  color: #222222;
  font-weight: 700;
  margin-bottom: 4px;
`;

const SubTitle = styled.div`
  font-size: 16px;
  color: #707070;
`;

const FormWrapper = styled.div`
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: start;

  margin: 32px 0;

  grid-gap: 24px;
`;

const StyledAutocompleteSelectWrapper = styled.div`
  width: 420px;
`;

const ProductListContent = styled.div`
  border-top: 1px solid #eaeaeabf;
  padding-top: 16px;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: start;

  width: 100%;

  grid-gap: 32px;
`;

const ProductListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: start;

  width: 100%;

  grid-gap: 16px;
`;

const FooterColumn = styled.div`
  border-top: 1px solid #eaeaeabf;

  padding-top: 16px;

  margin-top: 16px;

  display: flex;
  align-items: center;
  justify-content: start;
`;

interface Props {
  handleSelected: (vle: SettingsTab) => void;
}

export default function ThrivecartProducts(props: Props): JSX.Element | null {
  const { handleSelected } = props;
  const user = useRecoilValue(UserAtom);
  const [selectedIntegrations, setSelectedIntegrations] = useState<IOption>({
    name: "",
    value: "",
  });
  const [autoCompleteSuggestions, setAutoCompleteSuggestions] = useState<
    IOption[]
  >([]);

  const modal = useModal();

  const [tagsPool, setTagsPool] = useRecoilState(TagsAtom);

  const allIntegrations = useRecoilValue(AllIntegrationsAtom);
  const thriveIntegration = useMemo(
    () => allIntegrations.find((i) => i.type === "THRIVECART"),
    [allIntegrations]
  ) as IThrivecartIntegration;

  const [thriveProducts, setThriveProducts] = useState<IThrivecartProduct[]>(
    []
  );
  const [currentProductSelected, setCurrentProductSelected] =
    useState<IThrivecartProduct | null>(null);

  const [allIntegrationProducts, setAllIntegrationProducts] = useState<
    IProduct[]
  >([]);

  useEffect(() => {
    if (!thriveIntegration || thriveIntegration.type !== "THRIVECART")
      handleSelected(SettingsTab.INTEGRATIONS);

    (async () => {
      let [productsResult, upsellsResult, bumpsResult, downsellsResult] =
        await Promise.all([
          api.thrivecart.productsDetail(user!.userUid),
          api.thrivecart.upsellsDetail(user!.userUid),
          api.thrivecart.bumpsDetail(user!.userUid),
          api.thrivecart.downsellsDetail(user!.userUid),
        ]);

      const products = productsResult.data.data;
      const upsells = upsellsResult.data.data;
      const bumps = bumpsResult.data.data;
      const downsells = downsellsResult.data.data;

      if (products && upsells) {
        // Now, Thrive gives me products and upsells on separate
        // route, but the webhook on the backend is a little different.
        // So I use "product-" and "upsell-" as prefixes here to make it
        // easier

        let allThriveProducts: IThrivecartProduct[] = [
          ...products.map((p) => ({
            ...p,
            product_id: `product-${p.product_id}`,
            name: `Product -> ${p.name}`,
          })),
          ...upsells.map((u) => ({
            ...u,
            product_id: `upsell-${u.upsell_id}`,
            name: `Upsell -> ${u.name}`,
          })),
          ...downsells.map((u) => ({
            ...u,
            product_id: `downsell-${u.downsell_id}`,
            name: `Downsell -> ${u.name}`,
          })),
          ...bumps.map((u) => ({
            ...u,
            product_id: `bump-${u.bump_id}`,
            name: `Bump -> ${u.name}`,
          })),
        ];

        let allIntegrationProducts = await getAllIntegrationProducts();
        setAllIntegrationProducts(allIntegrationProducts);

        // Filter the already created ones
        allThriveProducts = allThriveProducts.filter(
          (tp) =>
            !allIntegrationProducts.find((ip) => ip.productId === tp.product_id)
        );

        setThriveProducts(allThriveProducts);
        setAutoCompleteSuggestions(
          allThriveProducts.map((p) => ({
            name: p.name,
            value: p.product_id,
          }))
        );
      }
    })();
  }, [thriveIntegration]);

  async function getAllIntegrationProducts() {
    const allIntegrationProducts = await ProductsAPI.get("THRIVECART");
    return allIntegrationProducts;
  }

  async function getTags() {
    const tags = await TagApi.readAll({ getCampaigns: true });
    setTagsPool(tags);
  }

  useEffect(() => {
    getTags();
  }, []);

  function handleSelectedSearch(savedSearchOption: IOption) {
    const product = thriveProducts.find(
      (p) => p.product_id === savedSearchOption.value
    );
    if (product) {
      setSelectedIntegrations({
        name: product.name,
        value: product.product_id,
      });
      setCurrentProductSelected(product);
    }
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (currentProductSelected) {
      await ProductsAPI.create({
        productId: currentProductSelected.product_id,
        productName: currentProductSelected.name,
        productTypeId: thriveIntegration.args.accountName,
        tagIds: [],
        type: ProductType.THRIVECART,
        userUid: user.userUid,
      });

      setSelectedIntegrations({
        name: "",
        value: "",
      });
      setCurrentProductSelected(null);

      let allIntegrationProducts = await getAllIntegrationProducts();

      // Filter the already created ones
      let allThriveProducts = thriveProducts.filter(
        (tp) =>
          !allIntegrationProducts.find((ip) => ip.productId === tp.product_id)
      );

      setAllIntegrationProducts(allIntegrationProducts);
      setThriveProducts(allThriveProducts);
      setAutoCompleteSuggestions(
        allThriveProducts.map((p) => ({
          name: p.name,
          value: p.product_id,
        }))
      );
    }
  };

  async function disconnectIntegration() {
    if (process.browser && thriveIntegration._id) {
      await IntegrationAPI.disconnect(thriveIntegration._id);
      handleSelected(SettingsTab.THRIVECART);
    }
  }

  function confirmDisconnectIntegration() {
    modal.pushModal(
      <Warning
        icon={"./icons/delete.svg"}
        title="Disconnect Integration"
        description="Are you sure you want to disconnect this integration?"
        buttonsName={["No", "Yes"]}
        onOkClicked={async () => {
          try {
            disconnectIntegration();
          } catch (e) {
            console.log(e);
          } finally {
            modal.popModal();
          }
        }}
        onCancelClicked={() => modal.popModal()}
      />
    );
  }

  return (
    <Container>
      <InfoColumn>
        <TextColumn>
          <Title>Thrivecart Products Integration</Title>
          <SubTitle>
            Please choose the products you wish to add tags from the list below.
          </SubTitle>
        </TextColumn>

        <img src="./images/thrivecart.png" alt="thrivecart" />
      </InfoColumn>

      <FormWrapper>
        <StyledAutocompleteSelectWrapper>
          <StyledAutocompleteSelect
            options={autoCompleteSuggestions}
            onOptionSelect={handleSelectedSearch}
            selectedOption={selectedIntegrations}
            placeholder="Enter the product name from Thrivecart"
          />
        </StyledAutocompleteSelectWrapper>

        <StyledButton onClick={handleSubmit}>Add</StyledButton>
      </FormWrapper>

      <ProductListContent>
        <Title>Products and Upsells</Title>

        <ProductListWrapper>
          {allIntegrationProducts.map((product, index) => (
            <ProductItem
              // I only need the first one to load the tags
              cancelTagLoading={index !== 0}
              key={product._id}
              product={product}
              getAllIntegrationProducts={async () => {
                let allIntegrationProducts = await getAllIntegrationProducts();

                // Filter the already created ones
                let allThriveProducts = thriveProducts.filter(
                  (tp) =>
                    !allIntegrationProducts.find(
                      (ip) => ip.productId === tp.product_id
                    )
                );

                setAllIntegrationProducts(allIntegrationProducts);
                setThriveProducts(allThriveProducts);
                setAutoCompleteSuggestions(
                  allThriveProducts.map((p) => ({
                    name: p.name,
                    value: p.product_id,
                  }))
                );
              }}
            />
          ))}
        </ProductListWrapper>
      </ProductListContent>

      <FooterColumn>
        <StyledButton
          css={css`
            background-color: transparent;
            border-color: red;
            color: red;

            &:hover {
              background-color: red;
              color: white;
            }

            transition: 300ms;
          `}
          onClick={confirmDisconnectIntegration}
        >
          Disconnect integration
        </StyledButton>
      </FooterColumn>
    </Container>
  );
}
